/**
 * Budget & coûts.
 *
 * Le relevé de l'opérateur (Twilio, Telnyx…) ne facture que la voix. Un appel
 * copiloté consomme en plus le flux audio et la transcription, facturés
 * ailleurs et jamais à l'appel : l'API les recompose et les rapproche du
 * relevé par identifiant d'appel. L'écran affiche donc partout le coût RÉEL —
 * voix + copilote — et garde le détail à côté, parce qu'un total sans
 * décomposition ne se vérifie pas.
 *
 * L'écran ne connaît pas l'opérateur : l'API lui donne son nom
 * (`provider_label`) et c'est tout ce qu'il affiche de lui.
 */
import { useCallback, useEffect, useState } from "react";
import { PhoneIncoming, PhoneOutgoing, RefreshCw, Save, MonitorSmartphone, Search,
  Radio, Mic } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { toast } from "sonner";
import { Loader } from "@/crm/ui";
import { cn } from "@/lib/utils";
import { CARD, PILL, THEAD, TR, inputCls, btnGhost } from "./shared";

const cur = (c) => { const u = (c || "").toUpperCase(); return u === "USD" ? "$" : u === "GBP" ? "£" : "€"; };
const fmt = (v, c, d = 2) => `${(v ?? 0).toFixed(d)} ${cur(c)}`;
const PERIOD_LABELS = { day: "Aujourd'hui", week: "Cette semaine", month: "Ce mois", year: "Cette année" };
const fmtDur = (s) => (s >= 60 ? `${Math.floor(s / 60)}m${String(s % 60).padStart(2, "0")}` : `${s}s`);
const STATUS_CLS = {
  completed: "bg-emerald-50 text-emerald-700", "no-answer": "bg-orange-50 text-orange-700",
  busy: "bg-orange-50 text-orange-700", failed: "bg-rose-50 text-rose-700", canceled: "bg-slate-100 text-slate-500",
};
const SELECT = "px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-600 outline-none focus:border-indigo-500";

// Décomposition lisible d'un appel : sans elle, un total ne se vérifie pas.
const copilotHint = (it, c) => (it.copilot
  ? `voix ${fmt(it.price, it.price_unit || c, 4)} · flux ${fmt(it.copilot.media_streams, c, 4)} · transcription ${fmt(it.copilot.stt, c, 4)}`
  : `voix ${fmt(it.price, it.price_unit || c, 4)} · appel non copiloté`);
const CHART_H = 140;
// Nom de l'opérateur tel que l'API le donne ; à défaut, un libellé neutre —
// l'écran ne présume pas de qui facture. Employé en apposition (« crédit
// Telnyx restant », « crédit opérateur restant »), jamais en sujet de phrase.
const OPERATEUR = "opérateur";

export default function TelephonyCosts() {
  const [summary, setSummary] = useState(null);
  const [calls, setCalls] = useState(null);
  const [period, setPeriod] = useState("month");
  const [direction, setDirection] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [via, setVia] = useState("all");
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [budgetInput, setBudgetInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [callsError, setCallsError] = useState(null);

  useEffect(() => { const t = setTimeout(() => setQDeb(q), 400); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setCallsError(null);
    const params = new URLSearchParams({ period, direction, status: statusF, via, limit: "200" });
    if (qDeb.trim()) params.set("q", qDeb.trim());
    if (dateFrom) params.set("start_date", dateFrom);
    if (dateTo) params.set("end_date", dateTo);
    // Deux lectures indépendantes : un détail des appels refusé par l'opérateur
    // ne doit pas priver l'écran des compteurs, et l'erreur affichée doit être
    // celle de l'API (« Erreur TELNYX : … »), pas le message générique d'Axios.
    const [s, c] = await Promise.allSettled([
      api.get("/twilio/costs/summary"),
      api.get(`/twilio/costs/calls?${params.toString()}`),
    ]);
    if (s.status === "fulfilled") {
      setSummary(s.value.data);
      setBudgetInput(s.value.data.budget?.monthly_budget ?? "");
    } else setError(apiError(s.reason?.response?.data?.detail ?? s.reason?.message));
    if (c.status === "fulfilled") setCalls(c.value.data);
    else { setCalls(null); setCallsError(apiError(c.reason?.response?.data?.detail ?? c.reason?.message)); }
    setLoading(false);
  }, [period, direction, statusF, via, qDeb, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const saveBudget = async () => {
    try {
      await api.put("/twilio/costs/budget", { monthly_budget: budgetInput === "" ? null : Number(budgetInput) });
      toast.success("Budget mensuel enregistré");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (error) return <div className="text-sm text-rose-600 bg-rose-50 rounded-xl p-4" data-testid="costs-error">{error}</div>;
  if (!summary) return <Loader label="Chargement des coûts…" />;

  const c = summary.currency;
  const operateur = summary.provider_label || OPERATEUR;
  const b = summary.budget || {};
  const month = summary.periods.month;
  const copilot = month.copilot || {};
  const realTotal = month.real_total ?? month.total_price;
  const avgPerCall = month.total_count ? realTotal / month.total_count : 0;

  return (
    <div data-testid="telephony-costs">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 className="font-heading font-bold text-lg text-slate-900">Budget & coûts</h3>
        <button data-testid="costs-refresh" onClick={load} disabled={loading} className={btnGhost}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>

      {/* Compteurs — même gabarit que Communications, sans icône */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4" data-testid="costs-summary">
        <Counter testid="costs-month-cost" label="Coût réel du mois" value={fmt(realTotal, c)}
          hint={`voix ${fmt(month.total_price, c)} · copilote ${fmt(copilot.total, c)}`} />
        <Counter testid="costs-month-minutes" label="Minutes consommées" value={String(month.total_minutes)}
          hint={b.monthly_budget ? `budget ${fmt(b.monthly_budget, c)}` : "aucun budget défini"} />
        <Counter testid="costs-avg-call" label="Coût moyen / appel" value={fmt(avgPerCall, c, 4)}
          hint={`tout compris, sur ${PERIOD_LABELS.month.toLowerCase()}`} />
        <Counter testid="costs-balance" label="Solde du compte" hint={`crédit ${operateur} restant`}
          value={summary.balance ? fmt(summary.balance.amount, summary.balance.currency) : "—"} />
      </div>

      {/* Budget mensuel */}
      <div className={cn(CARD, "p-4 mb-4")} data-testid="costs-budget-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[15px] font-medium text-slate-900">Budget mensuel</div>
            <div className="text-xs text-slate-400 mt-1">
              {fmt(b.month_spent, c)} consommés ce mois (voix + copilote)
              {b.monthly_budget ? ` sur ${fmt(b.monthly_budget, c)}` : " — définis un budget pour suivre ta consommation."}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="budget-input" type="number" min="0" step="0.5" placeholder="Aucun"
              value={budgetInput} onChange={(e) => setBudgetInput(e.target.value)}
              className={cn(inputCls, "w-28 text-right font-mono")} />
            <span className="text-sm text-slate-400">{cur(c)}</span>
            <button data-testid="budget-save" onClick={saveBudget} aria-label="Enregistrer le budget"
              className="p-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700"><Save size={15} /></button>
          </div>
        </div>
        {b.monthly_budget ? (
          <div className="mt-3.5">
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className={cn("h-full rounded-full transition-[width]",
                b.pct >= 100 ? "bg-rose-500" : b.pct >= 80 ? "bg-orange-500" : "bg-indigo-600")}
                style={{ width: `${Math.min(b.pct ?? 0, 100)}%` }} />
            </div>
            <div data-testid="budget-pct" className={cn("mt-1.5 text-xs",
              b.pct >= 100 ? "text-rose-600 font-medium" : b.pct >= 80 ? "text-orange-600" : "text-slate-400")}>
              {b.pct}% du budget de {fmt(b.monthly_budget, c)}{b.pct >= 100 && " — budget dépassé !"}
            </div>
          </div>
        ) : null}
      </div>

      <CopilotCosts month={copilot} prices={summary.copilot_prices} sources={summary.copilot_sources}
        ratesAsOf={summary.copilot_rates_as_of} currency={c} onSaved={load} operateur={operateur} />

      <PeriodChart periods={summary.periods} currency={c} operateur={operateur} />

      {/* Détail des appels */}
      <div className={cn(CARD, "overflow-hidden")}>
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <div className="text-[13.5px] font-medium text-slate-700 mr-auto">Détail des appels — coût réel</div>
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-2.5 py-1.5">
            <Search size={14} className="shrink-0 text-slate-400" />
            <input data-testid="costs-search" type="text" placeholder="Rechercher un numéro…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="w-36 min-w-0 bg-transparent text-sm outline-none" />
          </div>
          <select data-testid="costs-calls-period" value={period} onChange={(e) => setPeriod(e.target.value)}
            disabled={!!(dateFrom || dateTo)} className={cn(SELECT, "disabled:opacity-50")}>
            {Object.entries(PERIOD_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div className="flex items-center gap-1 text-xs text-slate-400">
            du <input data-testid="costs-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className={cn(SELECT, "font-mono")} />
            au <input data-testid="costs-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className={cn(SELECT, "font-mono")} />
          </div>
          <select data-testid="costs-calls-direction" value={direction} onChange={(e) => setDirection(e.target.value)} className={SELECT}>
            <option value="all">Tous</option>
            <option value="ENTRANT">Entrants</option>
            <option value="SORTANT">Sortants</option>
          </select>
          <select data-testid="costs-status-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)} className={SELECT}>
            <option value="all">Tous statuts</option>
            <option value="completed">Aboutis</option>
            <option value="no-answer">Sans réponse</option>
            <option value="busy">Occupé</option>
            <option value="failed">Échoués</option>
            <option value="canceled">Annulés</option>
          </select>
          <select data-testid="costs-via-filter" value={via} onChange={(e) => setVia(e.target.value)} className={SELECT}>
            <option value="all">Tous canaux</option>
            <option value="navigateur">Via navigateur</option>
            <option value="direct">Direct</option>
          </select>
        </div>

        {callsError ? (
          <p data-testid="costs-calls-error" className="m-3 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-600">
            Détail des appels indisponible — {callsError}
          </p>
        ) : !calls ? <Loader /> : calls.items.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">Aucun appel sur la période</p>
        ) : (
          <>
            <table className="w-full text-sm table-fixed hidden lg:table" data-testid="costs-table">
              <thead>
                <tr className={THEAD}>
                  <th className="px-5 py-2.5 text-left w-[148px]">Date</th>
                  <th className="px-3 py-2.5 text-left w-[132px]">Direction</th>
                  <th className="px-3 py-2.5 text-left">De</th>
                  <th className="px-3 py-2.5 text-left">Vers</th>
                  <th className="px-3 py-2.5 text-right w-[80px]">Durée</th>
                  <th className="px-3 py-2.5 text-left w-[112px]">Statut</th>
                  <th className="px-3 py-2.5 text-right w-[92px]">Voix</th>
                  <th className="px-3 py-2.5 text-right w-[100px]">Copilote</th>
                  <th className="px-5 py-2.5 text-right w-[104px]">Total</th>
                </tr>
              </thead>
              <tbody>
                {calls.items.map((it) => (
                  <tr key={it.sid} className={TR}>
                    <td className="px-5 py-2.5 font-mono text-xs text-slate-500 whitespace-nowrap">
                      {it.date ? new Date(it.date).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-3 py-2.5"><Direction it={it} /></td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-600 truncate">{it.from}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-600 truncate">{it.to}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-500">{fmtDur(it.duration)}</td>
                    <td className="px-3 py-2.5">
                      <span className={cn(PILL, STATUS_CLS[it.status] || "bg-slate-100 text-slate-600")}>{it.status}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-500">{fmt(it.price, it.price_unit || c, 4)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-500" title={copilotHint(it, c)}>
                      {it.copilot ? fmt(it.copilot.total, c, 4) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-2.5 text-right font-mono text-slate-900">{fmt(it.total_cost ?? it.price, c, 4)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-100 bg-slate-50/70">
                  <td colSpan={6} className="px-5 py-3 text-xs text-slate-400">
                    {calls.count} appel{calls.count > 1 ? "s" : ""} — les coûts {operateur} peuvent apparaître avec quelques
                    minutes de délai. « — » en colonne Copilote = appel sans transcription.
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-slate-500">{fmt(calls.total_price, c, 4)}</td>
                  <td className="px-3 py-3 text-right font-mono text-slate-500">{fmt(calls.total_copilot, c, 4)}</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-slate-900" data-testid="costs-total">
                    {fmt(calls.total_cost ?? calls.total_price, c, 4)}
                  </td>
                </tr>
              </tfoot>
            </table>

            {/* Cartes en dessous de lg */}
            <div className="flex flex-col gap-2.5 p-3 lg:hidden">
              {calls.items.map((it) => (
                <div key={it.sid} className="rounded-xl border border-slate-200 p-3.5">
                  <div className="flex items-center justify-between gap-3">
                    <Direction it={it} />
                    <span className="font-mono text-[11.5px] text-slate-400">
                      {it.date ? new Date(it.date).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </span>
                  </div>
                  <div className="mt-2.5 font-mono text-xs text-slate-600 truncate">{it.from} → {it.to}</div>
                  <div className="mt-2.5 flex items-center justify-between gap-3">
                    <span className={cn(PILL, STATUS_CLS[it.status] || "bg-slate-100 text-slate-600")}>{it.status}</span>
                    <span className="font-mono text-sm text-slate-900">
                      {fmtDur(it.duration)} · {fmt(it.total_cost ?? it.price, c, 4)}
                    </span>
                  </div>
                  <div className="mt-1 text-right text-[11.5px] text-slate-400">{copilotHint(it, c)}</div>
                </div>
              ))}
              <div className="flex items-center justify-between gap-3 px-1 pt-1 text-xs text-slate-400">
                <span>{calls.count} appel{calls.count > 1 ? "s" : ""}</span>
                <span className="font-mono font-bold text-slate-900">{fmt(calls.total_cost ?? calls.total_price, c, 4)}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Les deux postes que la facture d'appel ne montre pas : le flux audio (Media
 * Streams chez Twilio, Media Streaming chez Telnyx) et la transcription.
 *
 * Les montants viennent des relevés des fournisseurs, pas d'une grille saisie —
 * mais pas au même titre, et l'écran doit le dire : l'opérateur facture le
 * flux et publie le montant (relevé), Deepgram est prépayé et ne publie que
 * la quantité consommée (le montant reste le produit par le tarif du contrat).
 *
 * Et quand un relevé manque — clé Deepgram sans droit de lecture, compte qui
 * n'itemise pas les flux — le poste n'affiche pas zéro : il retombe sur ce que
 * le copilote a mesuré pendant les appels. D'où trois provenances possibles par
 * ligne, et un rapprochement entre le relevé et nos mesures juste en dessous.
 */

// Provenance d'un montant. Un relevé et une mesure ne se valent pas : la
// pastille doit permettre de le voir sans lire la note. Toute source qui n'est
// ni Deepgram, ni une mesure, ni « rien » est le relevé de l'opérateur —
// l'API la nomme par son fournisseur (`twilio`, `telnyx`).
const ORIGINS = {
  deepgram: { label: "Relevé Deepgram (quantité)", cls: "bg-emerald-50 text-emerald-700" },
  "mesuré": { label: "Mesuré par le copilote", cls: "bg-indigo-50 text-indigo-700" },
  aucun: { label: "Rien de consommé", cls: "bg-slate-100 text-slate-500" },
};
const originOf = (source, operateur) => ORIGINS[source]
  || (source ? { label: `Relevé ${operateur}`, cls: "bg-emerald-50 text-emerald-700" } : ORIGINS.aucun);

// Note du poste flux audio. Un relevé refusé (`unavailable`) n'est pas un relevé
// vide : la raison servie par l'API prime sur toute formule générique.
const mediaNote = (ms, operateur) => {
  const mesure = ms.measured
    ? "poste chiffré sur le flux mesuré appel par appel"
    : "aucun flux mesuré sur la période";
  if (ms.unavailable) return `${ms.reason || `relevé ${operateur} indisponible`} — ${mesure}`;
  if (ms.measured) return `aucune ligne de flux audio sur le relevé ${operateur} — ${mesure}`;
  if (ms.categories?.length) return `catégorie « ${ms.categories.join(", ")} » (${ms.matched_by})`;
  return "aucun flux facturé ni mesuré sur la période";
};

function CopilotCosts({ month, prices, sources, ratesAsOf, currency, onSaved, operateur = OPERATEUR }) {
  const [media, setMedia] = useState("");
  const [stt, setStt] = useState({});
  const [saving, setSaving] = useState(false);

  // La saisie repart des tarifs servis : recharger l'écran ne doit pas écraser
  // ce que l'administrateur vient de lire.
  useEffect(() => {
    setMedia(String(prices?.media_streams_per_minute ?? ""));
    setStt(Object.fromEntries(
      Object.entries(prices?.stt_per_minute || {}).map(([k, v]) => [k, String(v)])));
  }, [prices]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/copilot/pricing", {
        media_streams_per_minute: Number(media || 0),
        stt_per_minute: Object.fromEntries(
          Object.entries(stt).map(([k, v]) => [k, Number(v || 0)])),
      });
      toast.success("Tarifs de repli enregistrés");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const ms = month?.media_streams || {};
  const tr = month?.stt || {};
  const measured = month?.measured || {};
  const provider = tr.provider || "DEEPGRAM";
  const gap = (month?.total ?? 0) - (measured.total ?? 0);

  return (
    <div className={cn(CARD, "p-4 mb-4")} data-testid="copilot-costs-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="text-[15px] font-medium text-slate-900">Coût du copilote — ce mois</div>
        <div className="font-mono text-sm text-slate-900" data-testid="copilot-costs-total">
          {fmt(month?.total, currency, 4)}
        </div>
      </div>
      <p className="mt-1 text-xs text-slate-400 text-pretty">
        Ces deux postes n'apparaissent pas sur la facture d'un appel : l'opérateur agrège le
        flux audio dans ses relevés d'usage, la transcription est facturée sur un compte
        séparé. Les chiffres ci-dessous sont lus chez les fournisseurs.
      </p>

      {tr.http_status ? (
        <p data-testid="copilot-stt-warning"
          className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800 text-pretty">
          {tr.detail} Le poste reste compté dans le budget : il est chiffré sur l'audio
          mesuré pendant les appels, au tarif du contrat.
        </p>
      ) : null}

      <div className="mt-3.5 grid gap-2.5 sm:grid-cols-2">
        <Poste icon={Radio} testid="copilot-cost-media" label={`Flux audio ${operateur}`}
          amount={fmt(ms.amount, currency, 4)}
          detail={`${ms.minutes ?? 0} min ${ms.measured ? "de flux mesurées" : "facturées"}`}
          origin={originOf(ms.source, operateur)}
          note={mediaNote(ms, operateur)}
          unit={media} onUnit={setMedia} currency={currency}
          unitLabel="tarif de repli / min"
          unitHint={sources?.media_streams && sources.media_streams !== "contrat"
            ? `relevé : ${(prices?.media_streams_per_minute ?? 0).toFixed(6)} ${cur(currency)}/min appliqué aux appels`
            : "utilisé tant qu'aucun relevé n'est disponible"} />

        <Poste icon={Mic} testid="copilot-cost-stt"
          label={`Transcription ${provider === "DEEPGRAM" ? "Deepgram" : provider}`}
          amount={fmt(tr.amount, currency, 4)}
          detail={`${tr.minutes ?? 0} min d'audio${tr.requests ? ` · ${tr.requests} requête(s)` : ""}`}
          origin={originOf(tr.source, operateur)}
          note={tr.source === "deepgram"
            ? (tr.balance
              ? `solde restant ${fmt(tr.balance.amount, tr.balance.currency, 2)} — montant calculé au tarif du contrat`
              : "montant calculé au tarif du contrat (Deepgram est prépayé)")
            : tr.measured
              ? "relevé Deepgram indisponible — poste chiffré au tarif du contrat sur l'audio mesuré pendant les appels"
              : (tr.detail || "aucune transcription sur la période")}
          unit={stt[provider] ?? ""} onUnit={(v) => setStt((p) => ({ ...p, [provider]: v }))}
          currency={currency} unitLabel="tarif contrat / min"
          unitHint="Deepgram ne publie pas de montant par période : ce tarif fait foi" />
      </div>

      {/* Rapprochement : ce qui est retenu au budget face à nos mesures par appel. */}
      <div className="mt-2.5 rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-2.5"
        data-testid="copilot-costs-reconciliation">
        <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
          <span className="text-slate-500">
            Retenu au budget <span className="font-mono text-slate-800">{fmt(month?.total, currency, 4)}</span>
            {" · "}mesuré sur {measured.calls ?? 0} appel(s) copiloté(s){" "}
            <span className="font-mono text-slate-800">{fmt(measured.total, currency, 4)}</span>
          </span>
          <span className={cn("font-mono", Math.abs(gap) > 0.01 ? "text-orange-600" : "text-slate-400")}>
            écart {fmt(gap, currency, 4)}
          </span>
        </div>
        <p className="mt-1 text-[11.5px] text-slate-400 text-pretty">
          Un écart est normal quand les relevés répondent : ils couvrent tout le compte
          (appels non copilotés, essais) alors que nos mesures ne couvrent que les appels
          passés par le copilote. Écart nul = aucun relevé disponible, le budget est
          chiffré sur nos seules mesures.
        </p>
      </div>

      <div className="mt-3.5 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-slate-400">
          Tarifs de repli, dans la devise du compte {operateur} ({cur(currency)}). Le relevé du
          fournisseur prime quand il existe
          {ratesAsOf ? ` — dernier relevé ${new Date(ratesAsOf).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""}.
        </span>
        <button data-testid="copilot-pricing-save" onClick={save} disabled={saving}
          className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-60">
          <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer les tarifs"}
        </button>
      </div>
    </div>
  );
}

function Poste({ icon: Icon, testid, label, amount, detail, origin, note,
                unit, onUnit, unitLabel, unitHint, currency }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 p-3.5">
      <div className="flex items-center gap-2">
        <Icon size={15} className="shrink-0 text-indigo-600" />
        <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-slate-800">{label}</span>
        <span className="font-mono text-[13.5px] text-slate-900">{amount}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <span className={cn(PILL, origin.cls)}>{origin.label}</span>
        <span className="text-[11.5px] text-slate-500">{detail}</span>
      </div>
      <div className="mt-1 text-[11.5px] text-slate-400 text-pretty">{note}</div>
      <div className="mt-2.5 flex items-center gap-2">
        <input type="number" min="0" step="0.0001" value={unit} onChange={(e) => onUnit(e.target.value)}
          aria-label={`${label} — ${unitLabel}`}
          className={cn(inputCls, "w-28 text-right font-mono")} />
        <span className="text-[11.5px] text-slate-400">{cur(currency)} {unitLabel}</span>
      </div>
      {unitHint && <div className="mt-1 text-[11px] text-slate-400 text-pretty">{unitHint}</div>}
    </div>
  );
}

function Counter({ testid, label, value, hint }) {
  return (
    <div data-testid={testid} className={cn(CARD, "p-4")}>
      <span className="text-xs text-slate-500">{label}</span>
      <div className="mt-2.5 text-2xl font-mono text-slate-900">{value}</div>
      <div className="mt-1 text-[11.5px] text-slate-400">{hint}</div>
    </div>
  );
}

function Direction({ it }) {
  const inbound = it.direction === "ENTRANT";
  return (
    <span className={cn(PILL, inbound ? "bg-sky-50 text-sky-700" : "bg-indigo-50 text-indigo-700")}>
      {inbound ? <PhoneIncoming size={11} /> : <PhoneOutgoing size={11} />}
      {inbound ? "Entrant" : "Sortant"}
      {it.via_navigateur && <MonitorSmartphone size={11} className="text-slate-400" />}
    </span>
  );
}

// Barres empilées (sortants indigo-600, entrants indigo-200, copilote violet) —
// hauteurs en % de la période la plus élevée, sans librairie. Une période à 0
// reste une barre plate. Le copilote est un segment à part et non fondu dans le
// total : c'est le poste qu'on vient d'ajouter, il doit rester lisible seul.
function PeriodChart({ periods, currency, operateur = OPERATEUR }) {
  const rows = Object.entries(PERIOD_LABELS).map(([k, label]) => {
    const p = periods[k] || {};
    const out = p.outbound?.price || 0;
    const inb = p.inbound?.price || 0;
    const cop = p.copilot?.total || 0;
    return { k, label, out, inb, cop, total: out + inb + cop };
  });
  const max = rows.reduce((m, r) => Math.max(m, r.total), 0);

  return (
    <div className={cn(CARD, "p-4 mb-4")} data-testid="costs-chart">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="text-[15px] font-medium text-slate-900">Consommation par période</div>
        <div className="flex items-center gap-3.5 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px] bg-indigo-600" />Sortants</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px] bg-indigo-200" />Entrants</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px] bg-violet-400" />Copilote</span>
        </div>
      </div>
      <div className="mt-5 flex items-end gap-2.5" style={{ height: CHART_H + 44 }}>
        {rows.map((r) => {
          const h = max > 0 ? Math.round((r.total / max) * CHART_H) : 0;
          const share = (v) => (r.total > 0 ? Math.round((v / r.total) * h) : 0);
          const hOut = share(r.out);
          const hCop = share(r.cop);
          return (
            <div key={r.k} data-testid={`costs-period-${r.k}`} className="flex-1 min-w-0 flex flex-col items-center gap-2"
              title={`voix ${fmt(r.out + r.inb, currency, 4)} · copilote ${fmt(r.cop, currency, 4)}`}>
              <span className="font-mono text-[11px] text-slate-400">{fmt(r.total, currency)}</span>
              <div className="w-full max-w-[46px] flex flex-col justify-end gap-0.5" style={{ height: h }}>
                <span className="block rounded-t-[5px] bg-violet-400" style={{ height: hCop }} />
                <span className="block bg-indigo-200" style={{ height: h - hOut - hCop }} />
                <span className="block rounded-b-[5px] bg-indigo-600" style={{ height: hOut }} />
              </div>
              <span className="text-[11.5px] text-slate-500 text-center truncate w-full">{r.label}</span>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-slate-400 text-pretty">
        Les périodes sont cumulatives (le jour est inclus dans la semaine, la semaine dans le mois).
        Les montants sont le coût réel : voix {operateur} + flux audio + transcription.
      </p>
    </div>
  );
}
